/**
 * Priebeh zápasu. Udalosti nie sú jeden dlhý zoznam, ale **bloky po ťahoch** —
 * kto je práve na ťahu, sa v hre mení každých pár akcií a bez oddelenia sa
 * v tom po pár minútach nedá nič nájsť. Vnútri bloku nesie každý riadok farbu
 * podľa druhu (pohyb, karta, súboj, zranenie), aby sa dôležité veci dali
 * vychytať očami bez čítania.
 *
 * Karta spomenutá v riadku je zároveň **náhľad** — po prejdení myšou sa ukáže
 * celá, takže spätne vidno, čo súper vlastne zahral.
 */

import type { CardDef, GameEvent } from '@unmatched/engine';
import { useEffect, useRef } from 'react';

import { eventEntry, type LogEntry } from './eventText.js';

interface NamedState {
  cards: Record<string, { defId: string }>;
  fighters: { id: string; name: string }[];
}

interface Group {
  key: number;
  label: string;
  player: number | null;
  rows: { seq: number; entry: LogEntry }[];
}

interface Props {
  state: NamedState;
  events: GameEvent[];
  /** Meno hráča do hlavičky bloku (prezývka alebo postava). */
  nameOf: (player: number) => string;
  defOf: (cardId: string) => CardDef | null;
  onCardHover: (def: CardDef | null, rect?: DOMRect) => void;
}

export function LogPanel({ state, events, nameOf, defOf, onCardHover }: Props) {
  const scroller = useRef<HTMLDivElement>(null);
  const groups: Group[] = [];

  for (const event of events) {
    const entry = eventEntry(state, event);
    if (entry.kind === 'turn' || groups.length === 0) {
      groups.push({
        key: event.seq,
        label: entry.kind === 'turn' ? entry.parts.map((p) => p.text).join('') : 'Príprava',
        player: entry.kind === 'turn' ? entry.player : null,
        rows: [],
      });
      if (entry.kind === 'turn') continue;
    }
    groups[groups.length - 1]!.rows.push({ seq: event.seq, entry });
  }

  // Nové udalosti pribúdajú dole. Doskrolujeme len vtedy, keď hráč práve
  // nečíta staršiu časť logu — inak by mu text ušiel spod očí. Prvé naplnenie
  // (napr. história po refreshi) je výnimka: tam je koniec to podstatné.
  const filled = useRef(false);
  useEffect(() => {
    const el = scroller.current;
    if (!el || events.length === 0) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (!filled.current || atBottom) el.scrollTop = el.scrollHeight;
    filled.current = true;
  }, [events.length]);

  return (
    <aside className="log">
      <span className="eyebrow">Priebeh</span>

      <div className="log__scroll" ref={scroller}>
        {groups.map((group) => (
          <section key={group.key} className="log__group">
            <header
              className="log__turn"
              style={group.player !== null ? { ['--who' as string]: `var(--p${group.player})` } : undefined}
            >
              <span className="log__label">{group.label}</span>
              {group.player !== null ? <span className="log__who">{nameOf(group.player)}</span> : null}
            </header>

            <ol className="log__rows">
              {group.rows.map(({ seq, entry }) => (
                <li
                  key={seq}
                  className={`log__entry log__entry--${entry.kind}`}
                  style={entry.player !== null ? { ['--who' as string]: `var(--p${entry.player})` } : undefined}
                >
                  {entry.parts.map((part, index) => {
                    const def = part.cardId ? defOf(part.cardId) : null;
                    if (!def) return <span key={index}>{part.text}</span>;
                    return (
                      <button
                        key={index}
                        type="button"
                        className="log__card"
                        onMouseEnter={(e) => onCardHover(def, e.currentTarget.getBoundingClientRect())}
                        onMouseLeave={() => onCardHover(null)}
                        onFocus={(e) => onCardHover(def, e.currentTarget.getBoundingClientRect())}
                        onBlur={() => onCardHover(null)}
                      >
                        {part.text}
                      </button>
                    );
                  })}
                </li>
              ))}
            </ol>
          </section>
        ))}

        {events.length === 0 ? <p className="log__empty">Zatiaľ sa nič nestalo.</p> : null}
      </div>
    </aside>
  );
}
